import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import { api } from "@/lib/api";
import { SERVICE_ITEMS, STACK_ITEMS_ALL, PROJECT_ITEMS } from "@/lib/nav";
import { LinkedinLogo, XLogo, Article, MediumLogo, InstagramLogo, MastodonLogo } from "@phosphor-icons/react";

export default function Footer({ profile }) {
  const { lang, t } = useLang();
  const year = new Date().getFullYear();
  // Latest blog posts — surfaced in the footer site-links so crawlers can
  // discover new articles from every route without waiting for the /viden hub.
  const { data: posts = [] } = useQuery({ queryKey: ["blog"], queryFn: api.getBlog });
  const latestPosts = posts.slice(0, 6);

  const socials = [
    { icon: LinkedinLogo, url: profile?.linkedin, name: "LinkedIn" },
    { icon: XLogo, url: "https://x.com/MagnusLSchmidt", name: "X" },
    { icon: Article, url: "https://magnuslvschmidt.substack.com/", name: "Substack" },
    { icon: MediumLogo, url: "https://medium.com/@MagnusSchmidt", name: "Medium" },
    { icon: InstagramLogo, url: "https://www.instagram.com/magnusloeve/", name: "Instagram" },
    { icon: MastodonLogo, url: "https://mastodon.social/@magnusloev", name: "Mastodon" },
  ];

  return (
    <footer data-testid="footer" className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
      {/* Full site-links block — every service, stack, project and latest
          post rendered as a real <a href>. Crawlers can discover the entire
          site from any route without touching the (short) sidebar. */}
      <nav data-testid="footer-sitemap" aria-label={lang === "da" ? "Alle sider" : "All pages"}
        className="grid gap-10 pb-12 mb-12 border-b border-border md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="label-mono mb-4 text-muted-foreground/60">{t("nav_services")}</div>
          <ul className="space-y-1.5">
            {SERVICE_ITEMS.map((s) => (
              <li key={s.slug}>
                <Link to={buildPath("service", lang, { slug: s.slug })}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  {s[lang]}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="label-mono mb-4 text-muted-foreground/60">{t("nav_techstacks")}</div>
          <ul className="space-y-1.5">
            {STACK_ITEMS_ALL.map((s) => (
              <li key={s.slug}>
                <Link to={buildPath("techstack", lang, { slug: s.slug })}
                  data-testid={`footer-stack-${s.slug}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  {s[lang]}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="label-mono mb-4 text-muted-foreground/60">{t("nav_projects")}</div>
          <ul className="space-y-1.5">
            {PROJECT_ITEMS.map((p) => (
              <li key={p.slug}>
                <Link to={buildPath("project", lang, { slug: p.slug })}
                  data-testid={`footer-project-${p.slug}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  {p[lang]}
                </Link>
              </li>
            ))}
            <li className="pt-2">
              <Link to={buildPath("about", lang)}
                className="text-sm text-muted-foreground transition-colors hover:text-primary">
                {t("nav_about")}
              </Link>
            </li>
            <li>
              <Link to={buildPath("contact", lang)}
                className="text-sm text-muted-foreground transition-colors hover:text-primary">
                {t("nav_contact")}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="label-mono mb-4 text-muted-foreground/60">{lang === "da" ? "Seneste artikler" : "Latest articles"}</div>
          <ul className="space-y-1.5">
            {latestPosts.map((p) => {
              const c = p[lang] || p.da;
              return (
                <li key={p.slug}>
                  <Link to={buildPath("post", lang, { slug: p.slug })}
                    data-testid={`footer-post-${p.slug}`}
                    className="line-clamp-2 text-sm text-muted-foreground transition-colors hover:text-primary">
                    {c.title}
                  </Link>
                </li>
              );
            })}
            <li className="pt-2">
              <Link to={buildPath("blog", lang)}
                className="text-sm text-primary transition-colors hover:underline">
                {lang === "da" ? "Alle artikler →" : "All articles →"}
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      <div className="grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="font-heading text-2xl font-extrabold tracking-tight">Magnus Løv Schmidt</div>
          <p className="mt-3 max-w-sm text-sm text-muted-foreground font-light">{t("footer_tagline")}</p>
          <div className="mt-5 flex gap-2">
            {socials.map((s) => (
              <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                <s.icon size={17} weight="fill" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <div className="label-mono mb-4 text-muted-foreground/60">{t("contact_reach")}</div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>
              <a href={`mailto:${profile?.email}`} className="transition-colors hover:text-primary">{profile?.email}</a>
            </li>
            <li>{profile?.address?.street}</li>
            <li>{profile?.address?.postal_code} {profile?.address?.locality}</li>
            <li>{profile?.address?.region}</li>
          </ul>
        </div>

        <div>
          <div className="label-mono mb-4 text-muted-foreground/60">{lang === "da" ? "Nyhedsbrev" : "Newsletter"}</div>
          <p className="text-sm text-muted-foreground font-light leading-relaxed">
            {lang === "da"
              ? "Praktisk SEO-viden direkte i indbakken. 500+ læsere følger med."
              : "Practical SEO knowledge straight to your inbox. 500+ readers follow along."}
          </p>
          <a href={profile?.linkedin_newsletter || profile?.linkedin} target="_blank" rel="noopener noreferrer"
            data-testid="footer-newsletter"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary transition-colors hover:underline">
            {lang === "da" ? "Abonnér på LinkedIn" : "Subscribe on LinkedIn"} →
          </a>
        </div>
      </div>

      <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-border pt-6 text-xs text-muted-foreground/70 md:flex-row md:items-center">
        <span>© {year} Magnus Løv Schmidt. {t("footer_rights")}</span>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Link to={buildPath("sitemap", lang)} data-testid="footer-sitemap-link"
            className="transition-colors hover:text-primary">
            {t("nav_sitemap")}
          </Link>
          <a href="/sitemap.xml" data-testid="footer-sitemap-xml"
            className="font-mono transition-colors hover:text-primary">
            sitemap.xml
          </a>
          <span className="font-mono">{profile?.legal_name} · CVR {profile?.cvr} · Odense, Fyn</span>
        </div>
      </div>
    </footer>
  );
}
